import { Zoneless } from "./interfaces";
import { IsMacOS, IsiPadOS, IsiPhone, PlatformDetails, isMobile, Log } from "./dependencies";

const LOGTAG = "devicestateobserver";

/**
 * Types of window states that are observed by the DeviceStateObserver
 */
export const enum DeviceStateType {
    FOCUS,
    MOUSE_FOCUS,
    FULLSCREEN,
    ON_BATTERY,
    LOW_POWER
}

/**
 * Display topology enum that mimics what is in native code.
 * This enum is being declared here only for consistency.
 * Note that current implementation detects only EXTENDED mode.
 * For everything else, UNKNOWN will be returned.
 */
export const enum DisplayTopology {
    UNKNOWN,
    /** There is a single display online, and it is built-in to the computer (laptop / all-in-one) */
    INTERNAL,
    /** There are multiple displays online, and at least some of them are mirrored (same picture on two displays) */
    CLONE,
    /** There are multiple displays online, and at least 2 of them aren't in a mirror set (not cloned) */
    EXTENDED,
    /** There is a single display online, and it is external to the computer */
    EXTERNAL
}

/**
 * Explicit definition for experimental BatteryManager interface
 */
declare interface BatteryManager extends EventTarget {
    charging: boolean;
    chargingTime: number;
    dischargingTime: number;
    level: number;
}

declare interface ScreenExtended extends Screen, EventTarget {
    isExtended: boolean;
}

/**
 * This class is a centralized source for window states (e.g. focus, video states, visibility).
 * Other classes in the project should use this class to receive updates about the state and query the current value
 * and should not directly listen for or query them themselves.
 */
export class DeviceStateObserver {
    private deviceStateListeners: Map<DeviceStateType, Function[]> = new Map();

    private focusFunc: Function;
    private blurFunc: Function;
    private fullscreenFunc: Function;
    private mouseEnterFunc: Function;
    private mouseLeaveFunc: Function;
    private chargingChangeFunc: any;
    private batteryLevelChangeFunc: any;
    private displayTopologyChangeFunc: any;

    private windowFocus: boolean;
    private fullscreen: boolean = false;
    private mouseFocus: boolean;
    // default initialize to false, will be asynchronously updated
    private lowPower: boolean = false;
    private onBattery: boolean = false;

    private isMobile: boolean;
    private isiPhone: boolean;
    private isiPad: boolean;
    private isMac: boolean;

    private displayTopology: DisplayTopology;

    public constructor(platformDetails: PlatformDetails) {
        this.isMobile = isMobile(platformDetails);
        this.isiPhone = IsiPhone(platformDetails);
        this.isiPad = IsiPadOS(platformDetails);
        this.isMac = IsMacOS(platformDetails);
        // Assume initial mouse focus is the same as window focus, even though this may not be correct
        // There are no dedicated APIs to get the current mouse focus at any point in time
        // Indirect methods, such as mouse events or HTML hover states, have edge cases that they may miss
        // In most scenarios, the desync will be closed when the user first interacts with the stream
        this.windowFocus = this.mouseFocus = document.hasFocus();
        this.displayTopology = DisplayTopology.UNKNOWN;

        const zoneless = (window as any).zoneless as Zoneless;
        const windowAddEventListener =
            zoneless?.windowAddEventListener?.bind(window) ?? window.addEventListener.bind(window);
        const documentAddEventListener =
            zoneless?.documentAddEventListener?.bind(document) ??
            document.addEventListener.bind(document);

        this.focusFunc = this.onFocus.bind(this);
        this.blurFunc = this.onBlur.bind(this);
        this.fullscreenFunc = this.onFullscreenChange.bind(this);
        this.mouseEnterFunc = this.onMouseEnter.bind(this);
        this.mouseLeaveFunc = this.onMouseLeave.bind(this);
        this.chargingChangeFunc = this.onChargingChange.bind(this);
        this.batteryLevelChangeFunc = this.onBatteryLevelChange.bind(this);
        this.displayTopologyChangeFunc = this.onDisplayTopologyChange.bind(this);

        windowAddEventListener("focus", this.focusFunc);
        windowAddEventListener("blur", this.blurFunc);
        [
            "fullscreenchange",
            "webkitfullscreenchange",
            "mozfullscreenchange",
            "msfullscreenchange"
        ].forEach(eventType => documentAddEventListener(eventType, this.fullscreenFunc));
        documentAddEventListener("mouseenter", this.mouseEnterFunc);
        documentAddEventListener("mouseleave", this.mouseLeaveFunc);

        if (this.isBatterySupported()) {
            (navigator as any).getBattery().then((battery: BatteryManager) => {
                this.setOnBattery(!battery.charging);
                this.setLowPower(this.isBatteryLow(battery));
                battery.addEventListener("chargingchange", this.chargingChangeFunc);
                battery.addEventListener("levelchange", this.batteryLevelChangeFunc);
            });
        }

        if (this.isWindowManagementSupported()) {
            // Initialize at the beginning
            this.onDisplayTopologyChange();
            (<ScreenExtended>window.screen).addEventListener(
                "change",
                this.displayTopologyChangeFunc
            );
        }

        this.deviceStateListeners.set(DeviceStateType.FOCUS, []);
        this.deviceStateListeners.set(DeviceStateType.FULLSCREEN, []);
        this.deviceStateListeners.set(DeviceStateType.MOUSE_FOCUS, []);
        this.deviceStateListeners.set(DeviceStateType.ON_BATTERY, []);
        this.deviceStateListeners.set(DeviceStateType.LOW_POWER, []);
        // update with current fullscreen state
        this.onFullscreenChange();
    }

    /**
     * Uninitialize the window state observer
     * @note Must be called before class leaves scope
     */
    public uninitialize() {
        const zoneless = (window as any).zoneless as Zoneless;
        const windowRemoveEventListener =
            zoneless?.windowRemoveEventListener?.bind(window) ??
            window.removeEventListener.bind(window);
        const documentRemoveEventListener =
            zoneless?.documentRemoveEventListener?.bind(document) ??
            document.removeEventListener.bind(document);

        windowRemoveEventListener("focus", this.focusFunc);
        windowRemoveEventListener("blur", this.blurFunc);
        [
            "fullscreenchange",
            "webkitfullscreenchange",
            "mozfullscreenchange",
            "msfullscreenchange"
        ].forEach(eventType => documentRemoveEventListener(eventType, this.fullscreenFunc));
        documentRemoveEventListener("mouseenter", this.mouseEnterFunc);
        documentRemoveEventListener("mouseleave", this.mouseLeaveFunc);

        if (this.isBatterySupported()) {
            (navigator as any).getBattery().then((battery: BatteryManager) => {
                battery.removeEventListener("chargingchange", this.chargingChangeFunc);
                battery.removeEventListener("levelchange", this.batteryLevelChangeFunc);
            });
        }

        if (this.isWindowManagementSupported()) {
            (<ScreenExtended>window.screen).removeEventListener(
                "change",
                this.displayTopologyChangeFunc
            );
        }

        for (const listeners of this.deviceStateListeners.values()) {
            listeners.splice(0);
        }
    }

    /**
     * Get whether or not the application window has focus
     * @returns Whether the application window has focus
     */
    public getWindowFocus(): boolean {
        return this.windowFocus;
    }

    /**
     * Set the window focus state
     * @param focus Value to set the window focus state to
     * @note Should only be used in rare cases, where the application is aware of the state before the browser fires the corresponding events.
     */
    public setWindowFocus(focus: boolean) {
        // On mobile devices, the default use case does not have a pointing device connected at all times
        // Instead of only relying on mouseenter/mouseleave, use window focus as a fallback
        if (this.isMobile) {
            this.setMouseFocus(focus);
        }
        if (this.windowFocus !== focus) {
            this.windowFocus = focus;
            for (const listener of this.deviceStateListeners.get(DeviceStateType.FOCUS)!) {
                listener(focus);
            }
        }
    }

    /**
     * Get whether or not the application window is in fullscreen
     * @returns Whether the application window is in fullscreen
     */
    public getFullscreen(): boolean {
        return this.fullscreen;
    }

    private setFullscreen(fullscreen: boolean) {
        if (this.fullscreen !== fullscreen) {
            this.fullscreen = fullscreen;
            for (const listener of this.deviceStateListeners.get(DeviceStateType.FULLSCREEN)!) {
                listener(fullscreen);
            }
        }
    }
    /**
     * Get whether or not the application window has mouse focus
     * @returns Whether the application window has mouse focus
     */
    public getMouseFocus(): boolean {
        return this.mouseFocus;
    }

    private setMouseFocus(focus: boolean) {
        if (this.mouseFocus !== focus) {
            this.mouseFocus = focus;
            for (const listener of this.deviceStateListeners.get(DeviceStateType.MOUSE_FOCUS)!) {
                listener(focus);
            }
        }
    }
    /**
     * Get whether or not the device is on low battery
     * @returns Whether the device is on low battery
     */
    public getLowPower(): boolean {
        return this.lowPower;
    }

    private setLowPower(lowPower: boolean) {
        if (this.lowPower !== lowPower) {
            this.lowPower = lowPower;
            for (const listener of this.deviceStateListeners.get(DeviceStateType.LOW_POWER)!) {
                listener(lowPower);
            }
        }
    }
    /**
     * Get whether or not the device is on battery
     * @returns Whether the device is on battery
     */
    public getOnBattery(): boolean {
        return this.onBattery;
    }

    private setOnBattery(onBattery: boolean) {
        if (this.onBattery !== onBattery) {
            this.onBattery = onBattery;
            for (const listener of this.deviceStateListeners.get(DeviceStateType.ON_BATTERY)!) {
                listener(onBattery);
            }
        }
    }

    public getDisplayTopology(): DisplayTopology {
        return this.displayTopology;
    }

    /**
     * Register a listener for a window state
     * @param type Type of window state to listen for
     * @param listener Listener that will be called when the state is updated
     * @note Should be accompanied by a call to removeStateListener before listener goes out of scope to prevent leaks
     */
    public addStateListener(type: DeviceStateType, listener: Function) {
        this.deviceStateListeners.get(type)!.push(listener);
    }

    /**
     * Unregister a listener for a window state
     * @param type Type of window state to listen for
     * @param listener Listener that was registered in addStateListener
     */
    public removeStateListener(type: DeviceStateType, listener: Function) {
        const listeners = this.deviceStateListeners.get(type)!;
        const index = listeners.indexOf(listener);
        if (index !== -1) {
            listeners.splice(index, 1);
        }
    }

    /**
     * Checks whether or not this device supports observation of the battery state
     * @return true if this device can monitor the battery state and provide ON_BATTERY and LOW_POWER events
     */
    public isBatterySupported(): boolean {
        return (navigator as any).getBattery;
    }

    public isWindowManagementSupported(): boolean {
        return (<ScreenExtended>window.screen).isExtended !== undefined;
    }

    private onFocus() {
        this.setWindowFocus(true);
    }

    private onBlur() {
        this.setWindowFocus(false);
    }

    private onFullscreenChange() {
        const doc: any = window.document;
        const fullscreen = !!(
            document.fullscreen ||
            doc.webkitIsFullScreen == true ||
            doc.mozFullScreen ||
            doc.msFullscreenElement
        );
        this.setFullscreen(fullscreen);
    }

    private onMouseEnter() {
        this.setMouseFocus(true);
    }

    private onMouseLeave() {
        this.setMouseFocus(false);
    }

    private onChargingChange() {
        (navigator as any).getBattery().then((battery: BatteryManager) => {
            this.setOnBattery(!battery.charging);
        });
    }

    private onBatteryLevelChange() {
        (navigator as any).getBattery().then((battery: BatteryManager) => {
            this.setLowPower(this.isBatteryLow(battery));
        });
    }

    private isBatteryLow(battery: BatteryManager): boolean {
        // TODO: Determine low battery threshold for specific devices
        if (this.isMac) {
            // Macs give warning about low battery at 22% with kIOPSLowBatteryWarningEarly
            // https://opensource.apple.com/source/IOKitUser/IOKitUser-1445.71.1/ps.subproj/IOPowerSources.h.auto.html
            return battery.level <= 0.22;
        }
        if (this.isiPhone) {
            // iPhones indicates low battery to the user at 20%
            return battery.level <= 0.2;
        }
        if (this.isiPad) {
            // iPads indicates low battery to the user at 10%
            return battery.level <= 0.1;
        }
        // Windows sends a low battery event at 33% percent
        // https://learn.microsoft.com/en-us/windows/win32/api/winbase/ns-winbase-system_power_status
        // Use Windows level as the fallback since it is the highest tracked threshold
        return battery.level <= 0.33;
    }

    private onDisplayTopologyChange() {
        const screen = <ScreenExtended>window.screen;
        if (screen.isExtended) {
            this.displayTopology = DisplayTopology.EXTENDED;
        } else {
            this.displayTopology = DisplayTopology.UNKNOWN;
        }
        Log.i("{0c4eeb2}", "{ee99c72}", this.displayTopology);
    }
}
